<template>
  <div class="flex flex-col gap-[9px]">
    <div class="flex items-center justify-between gap-4 text-[14px] font-bold text-white">
      <span>{{ label }}</span>
      <span>{{ displayed }}%</span>
    </div>
    <div
      class="h-[10px] w-full overflow-hidden rounded-full bg-blue-soft"
      role="progressbar"
      :aria-valuenow="percentage"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="label"
    >
      <div
        class="progress-fill h-full w-full rounded-full bg-orange-soft"
        :class="{ 'progress-fill-busy': busy }"
        :style="{ transform: `translateX(${percentage - 100}%)`, '--nm-bar-ms': `${transitionMs}ms` }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'

import { prefersReducedMotion } from '@/stores/animation'

const props = withDefaults(
  defineProps<{
    label: string
    percentage: number
    /** How long the bar may take to reach the percentage; 0 puts it there directly. */
    transitionMs?: number
    /** The calculation is running: the fill carries a sweeping sheen. */
    busy?: boolean
  }>(),
  { transitionMs: 0, busy: false },
)

// The number counts along with the bar; linear, like the bar itself, so the two stay in step. The
// screen reader value on the bar carries the target directly, a counting number only rattles there.
const displayed = ref(Math.round(props.percentage))

let frame = 0

watch(
  () => props.percentage,
  (target) => {
    cancelAnimationFrame(frame)

    if (props.transitionMs <= 0 || prefersReducedMotion()) {
      displayed.value = Math.round(target)

      return
    }

    const from = displayed.value
    const started = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - started) / props.transitionMs, 1)
      displayed.value = Math.round(from + (target - from) * progress)

      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      }
    }

    frame = requestAnimationFrame(tick)
  },
)

onBeforeUnmount(() => cancelAnimationFrame(frame))
</script>
